import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBilling } from './hooks/useBilling';
import {
  WalletBalance,
  TransactionHistory,
  TopUp,
  PaymentMethods,
  AutoRechargeSettings,
  UsageMetrics,
  Pricing,
} from './components';
import { PageLoader, FullWidthContainer, Alert } from '../../components';
import { useToast } from '../../contexts/useToast';

const ERROR_AUTO_DISMISS_TIME = 5000; // 5 seconds

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    wallet,
    transactions,
    paymentMethods,
    autoRechargeSettings,
    usageSummary,
    usageRecords,
    pricingProfile,
    filters,
    pagination,
    loading,
    error,
    setFilters,
    loadWallet,
    validateCoupon,
    handleTopUp,
    saveAutoRechargeSettings,
    setDefaultPaymentMethod,
    removePaymentMethod,
    exportTransactions,
    handleUsageDateRangeChange,
    checkPaymentStatus,
    loadTransactions,
    loadPricingProfile,
    clearError,
    loadPaymentMethods,
    attachPaymentMethod,
  } = useBilling();

  const [activeTab, setActiveTab] = useState<
    'wallet' | 'pricing' | 'transactions' | 'topup' | 'payments' | 'auto-recharge' | 'usage'
  >('wallet');
  const [showError, setShowError] = useState(false);
  const clearErrorRef = useRef(clearError);
  clearErrorRef.current = clearError;

  useEffect(() => {
    if (activeTab === 'wallet') {
      loadWallet();
    } else if (activeTab === 'transactions') {
      loadTransactions();
    } else if (activeTab === 'pricing') {
      loadPricingProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Show error when it changes and handle auto-dismiss
  useEffect(() => {
    if (error && activeTab !== 'topup' && activeTab !== 'auto-recharge') {
      setShowError(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setShowError(false);
        clearErrorRef.current();
      }, ERROR_AUTO_DISMISS_TIME);
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error, activeTab]);

  // Clear error when switching tabs
  useEffect(() => {
    if (activeTab !== 'topup' && activeTab !== 'auto-recharge' && error) {
      clearErrorRef.current();
      setShowError(false);
    }
  }, [activeTab, error]);

  // Hide error when user starts a new action
  useEffect(() => {
    if (loading) {
      setShowError(false);
    }
  }, [loading]);

  const handleDismissError = () => {
    setShowError(false);
    clearErrorRef.current(); // Clear error from hook state
  };

  // Handle Stripe Checkout return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment_status');

    if (sessionId) {
      // Check payment status
      checkPaymentStatus(sessionId).then(data => {
        if (data?.status === 'succeeded') {
          // Clean up URL
          window.history.replaceState({}, '', window.location.pathname);
        } else if (data?.status === 'failed' || paymentStatus === 'failed') {
          showToast({ type: 'error', message: 'Payment failed. Please try again.' });
          window.history.replaceState({}, '', window.location.pathname);
        } else if (paymentStatus === 'canceled') {
          showToast({ type: 'info', message: 'Payment was canceled.' });
          window.history.replaceState({}, '', window.location.pathname);
        }
      });
    }
  }, [checkPaymentStatus, showToast, t]);

  if (loading && !wallet) {
    return <PageLoader show={true} />;
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('billing.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('billing.subtitle')}</p>
          </div>
        </div>

        {error && activeTab !== 'topup' && activeTab !== 'auto-recharge' && showError && (
          <Alert variant="error" title="Error" dismissible onDismiss={handleDismissError}>
            {error}
          </Alert>
        )}

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8" aria-label="Tabs">
            {[
              { id: 'wallet', label: t('billing.wallet'), icon: 'account_balance_wallet' },
              { id: 'pricing', label: t('billing.pricing'), icon: 'price_check' },
              { id: 'transactions', label: t('billing.transactions'), icon: 'receipt_long' },
              { id: 'topup', label: t('billing.topUp'), icon: 'add_circle' },
              { id: 'payments', label: t('billing.payments'), icon: 'credit_card' },
              { id: 'auto-recharge', label: t('billing.autoRecharge'), icon: 'autorenew' },
              { id: 'usage', label: t('billing.usage'), icon: 'bar_chart' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                <span className="material-icons text-lg">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'wallet' && (
          <WalletBalance
            wallet={wallet}
            loading={loading}
            onRefresh={loadWallet}
            lowBalanceThreshold={20}
          />
        )}

        {activeTab === 'pricing' && (
          <Pricing
            profile={pricingProfile}
            loading={loading}
            error={error}
            onLoad={loadPricingProfile}
          />
        )}

        {activeTab === 'transactions' && (
          <TransactionHistory
            transactions={transactions}
            loading={loading}
            filters={filters}
            onFiltersChange={setFilters}
            onExport={exportTransactions}
            pagination={{
              ...pagination,
              onNext: pagination.next ? () => loadTransactions(pagination.next!) : undefined,
              onPrevious: pagination.previous
                ? () => loadTransactions(pagination.previous!)
                : undefined,
            }}
          />
        )}

        {activeTab === 'topup' && (
          <TopUp
            onTopUp={handleTopUp}
            onValidateCoupon={validateCoupon}
            paymentMethods={paymentMethods}
            loading={loading}
            error={error}
            onClearError={clearError}
            onSuccess={async () => {
              // Small delay to allow webhook to process
              await new Promise(resolve => setTimeout(resolve, 1500));
              await loadWallet();
            }}
          />
        )}

        {activeTab === 'payments' && (
          <PaymentMethods
            paymentMethods={paymentMethods}
            onAddMethod={async () => {
              await loadPaymentMethods();
              showToast({ type: 'success', message: 'Payment method added successfully!' });
            }}
            onEditMethod={() => {
              showToast({
                type: 'info',
                message:
                  'Payment method editing is not available. Please remove and add a new one.',
              });
            }}
            onRemoveMethod={removePaymentMethod}
            onSetDefault={setDefaultPaymentMethod}
            onAttachPaymentMethod={attachPaymentMethod}
          />
        )}

        {activeTab === 'auto-recharge' && (
          <AutoRechargeSettings
            settings={autoRechargeSettings}
            paymentMethods={paymentMethods}
            loading={loading}
            onSave={async settings => {
              try {
                const result = await saveAutoRechargeSettings(settings);
                if (result?.success) {
                  showToast({ type: 'success', message: t('billing.autoRechargeSaveSuccess') });
                }
              } catch {
                // Error is already handled by saveAutoRechargeSettings
              }
            }}
            error={error}
            onClearError={clearError}
          />
        )}

        {activeTab === 'usage' && (
          <UsageMetrics
            summary={usageSummary}
            records={usageRecords}
            loading={loading}
            onDateRangeChange={handleUsageDateRangeChange}
          />
        )}
      </div>
    </FullWidthContainer>
  );
};

export { Billing };
